import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cityStay, eventStay, STAY22_SOURCE, stay22MapUrl, stay22Url, type Stay } from '@todo/core/hotels';
import { localizedTitle } from '@todo/core/i18n/event-content';
import { formatDayMonth } from '@todo/core/i18n/format';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal, type OutboundMeta } from '@todo/core/open-external';
import type { DbEvent } from '@todo/core/supabase';
import type { SiteConfig } from '@todo/core/site-config';
import { SITE } from '@site';

import { ICONS, Icon } from './icon';
import { NavLink } from './nav-link';
import { localePath } from '../lib/format';

// The tour-facts look, as the show page's other sections.
import factsCss from './tour-facts.module.css';
import css from './hotels.module.css';

const SITE_CONFIG: SiteConfig = SITE;

// "Where to stay": a hotel search around the venue or city via Stay22
// (@todo/core/hotels; only on a site with SITE.stay22). The search is a
// <button>, not an <a href>: the link is built on click, so its dates are
// settled against today and crawlers see no outbound URL. Under it, the same
// search as Stay22's map widget — an iframe mounted only once the visitor asks
// for it, so no page loads Stay22's code (a full map app with its own
// analytics) unasked. Strings: `hotels.*` in the app's resources.

/** A YYYY-MM-DD day as "24 Oct" — as a UTC noon, so no timezone can shift it. */
function formatDay(day: string, locale: Locale): string {
  return formatDayMonth(new Date(`${day}T12:00:00Z`), 'UTC', locale);
}

// detail-layout.module.css's desktop breakpoint: from here the detail page's
// panel is a sticky side column.
const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * A map opened from the detail page's side panel, which on desktop is sticky:
 * a 420px map in it would push the panel past the screen, so the page renders
 * the map in its main column instead (HotelsMapFrame `wide`).
 */
export type WideHotelsMap = { src: string | null; setSrc: (src: string | null) => void };

/**
 * Stay22's map widget. It scrolls itself into view when it opens: `wide` (the
 * main column, away from the button) is centred; under the button it scrolls
 * only as far as needed, which keeps the button in view. The sandbox lets the
 * widget run and open bookings in a new tab, but never navigate this page.
 */
export function HotelsMapFrame({ src, wide }: { src: string; wide?: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: wide ? 'center' : 'nearest' });
  }, [src, wide]);

  return (
    <iframe
      ref={ref}
      className={wide ? `${css.map} ${css.wideMap}` : css.map}
      src={src}
      title={t('hotels.mapTitle')}
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
      referrerPolicy="strict-origin-when-cross-origin"
    />
  );
}

function HotelsActions({
  stay,
  campaign,
  meta,
  label,
  zoom,
  wideMap,
}: {
  stay: Stay;
  /** Stay22's report key, e.g. `event_dubai` (the map's gets `_map`). */
  campaign: string;
  meta: Omit<OutboundMeta, 'kind' | 'source' | 'locale'>;
  label: string;
  /** Of the map under the button. */
  zoom: number;
  /** Desktop: the map goes to the page's main column (see WideHotelsMap). */
  wideMap?: WideHotelsMap;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [mapSrc, setMapSrc] = useState<string | null>(null);
  const isOpen = mapSrc != null || wideMap?.src != null;

  // Where the map goes is decided at click time, so the static HTML is the
  // same on every screen.
  const toggle = () => {
    if (isOpen) {
      setMapSrc(null);
      wideMap?.setSrc(null);
      return;
    }
    const src = stay22MapUrl(stay, { campaign, locale, zoom });
    if (wideMap && window.matchMedia(DESKTOP_QUERY).matches) wideMap.setSrc(src);
    else setMapSrc(src);
  };

  return (
    <>
      <div className={css.actions}>
        <button
          type="button"
          className={css.button}
          onClick={() =>
            openExternal(stay22Url(stay, { campaign, locale }), {
              kind: 'hotel',
              source: STAY22_SOURCE,
              locale,
              ...meta,
            })
          }>
          <Icon d={ICONS.bed} size={20} />
          {label}
        </button>
        <button type="button" className={css.toggle} aria-expanded={isOpen} onClick={toggle}>
          <Icon d={ICONS.map} size={18} />
          {isOpen ? t('hotels.hideMap') : t('hotels.showMap')}
        </button>
      </div>
      {mapSrc ? <HotelsMapFrame src={mapSrc} /> : null}
    </>
  );
}

/**
 * The show page's "Staying the night?", under the ticket CTA in the side
 * panel: hotels near the venue for the night of the event (the show's soonest
 * date). The caller hides it once the show is past, and renders `wideMap`'s
 * map in its main column (HotelsMapFrame `wide`).
 */
export function EventHotels({ event, wideMap }: { event: DbEvent; wideMap?: WideHotelsMap }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const stay = eventStay(event);
  if (!stay) return null;

  const where = stay.atVenue && event.venue_name ? t('hotels.nearVenue', { venue: event.venue_name }) : t('hotels.inCity');
  const dates =
    stay.checkin && stay.checkout
      ? t('hotels.dates', { checkin: formatDay(stay.checkin, locale), checkout: formatDay(stay.checkout, locale) })
      : null;

  return (
    <div className={css.eventBlock}>
      <p className={css.eventHeading}>{t('hotels.eventHeading')}</p>
      <p className={css.hint}>{[where, dates].filter(Boolean).join(' · ')}</p>
      <HotelsActions
        stay={stay}
        campaign={`event_${event.city_id ?? SITE_CONFIG.stay22?.region}`}
        meta={{ id: event.id, title: localizedTitle(event, locale), city: event.city_id }}
        label={t('hotels.cta')}
        zoom={14}
        wideMap={wideMap}
      />
    </div>
  );
}

/**
 * "Where to stay in Dubai": a card for pages that know only the city (guides).
 * No dates; the visitor picks them on the booking site. Nothing for a city
 * without a centroid in SITE.stay22.
 */
export function CityHotels({
  cityId,
  campaign,
  pageId,
  areasPath,
}: {
  cityId: string | null | undefined;
  /** Which kind of page, for Stay22's reports: 'article' | 'city'. */
  campaign: string;
  /** Umami `id` of the click: the page it came from (article _id, city id). */
  pageId: string;
  /** The site's "where to stay" page under the locale, linked as `hotels.areasLink`. */
  areasPath?: string;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const stay = cityStay(cityId);
  if (!stay || !cityId) return null;

  return (
    <section className={css.card}>
      <h2 className={factsCss.heading}>{t('hotels.sectionHeading')}</h2>
      <p className={css.text}>{t('hotels.sectionText')}</p>
      <HotelsActions
        stay={stay}
        campaign={`${campaign}_${cityId}`}
        meta={{ id: pageId, city: cityId }}
        label={t('hotels.ctaCity')}
        zoom={12}
      />
      {areasPath ? (
        <p className={css.areasLink}>
          <NavLink href={localePath(locale, areasPath)}>
            {t('hotels.areasLink')} <Icon d={ICONS.arrowLong} size={16} />
          </NavLink>
        </p>
      ) : null}
    </section>
  );
}
